import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { buildApiUrl } from "../../lib/api";
import "./ExecutionAnalysis.css";

const DAYS_OPTIONS = [
    { label: "30d", value: 30 },
    { label: "60d", value: 60 },
    { label: "90d", value: 90 },
];

const STRATEGY_LABELS = {
    wheel:           "Wheel Strategy",
    earnings:        "Earnings Strategy",
    tactical_spread: "Tactical Spread",
    regime_spread:   "Regime Spread",
    1: "Wheel Strategy",
    2: "Tactical Spread",
    3: "Regime Spread",
    4: "Earnings Strategy",
};

const EA_ACTION_LABELS = {
    SELL_PUT:           "Sell Put",
    SELL_CSP:           "Sell Put",
    SELL_CALL:          "Sell Call",
    BUY_TO_CLOSE:       "Close",
    TAKE_PROFIT:        "Close",
    OPEN_DEBIT_SPREAD:  "Open Spread",
    CLOSE_DEBIT_SPREAD: "Close Spread",
    CLOSE:              "Close",
};

const SPREAD_ACTIONS = new Set(["OPEN_DEBIT_SPREAD", "CLOSE_DEBIT_SPREAD"]);
const WHEEL_ACTIONS  = new Set(["SELL_PUT", "SELL_CSP", "SELL_CALL", "BUY_TO_CLOSE", "TAKE_PROFIT", "CLOSE"]);

function inferStrategyLabel(action) {
    if (!action) return null;
    if (SPREAD_ACTIONS.has(action)) return "Tactical Spread";
    if (WHEEL_ACTIONS.has(action))  return "Wheel Strategy";
    return null;
}

function resolveTradeStrategy(trade) {
    const key = trade.strategy || null;
    if (key && STRATEGY_LABELS[key]) return STRATEGY_LABELS[key];
    if (trade.strategy_id != null && STRATEGY_LABELS[trade.strategy_id]) return STRATEGY_LABELS[trade.strategy_id];
    return inferStrategyLabel(trade.action);
}

function resolveTradeMode(trade) {
    const mode = (trade.automation_mode || "").toLowerCase();
    if (mode === "auto")                       return "auto";
    if (mode === "manual" || mode === "semi")  return "manual";
    return null;
}

function fmt2(n) {
    if (n == null) return "—";
    return Number(n).toFixed(2);
}

function fmtPct(n) {
    if (n == null) return "—";
    return `${Number(n) >= 0 ? "+" : ""}${Number(n).toFixed(2)}%`;
}

function fmtDollars(n) {
    if (n == null) return "—";
    return `${Number(n) >= 0 ? "+" : ""}$${Math.abs(Number(n)).toFixed(2)}`;
}

function fmtSeconds(n) {
    if (n == null) return "—";
    const s = Number(n);
    if (s < 60) return `${s.toFixed(0)}s`;
    return `${(s / 60).toFixed(1)}m`;
}

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    } catch {
        return iso.slice(0, 10);
    }
}

function LoadingDots() {
    return (
        <div className="ea-loading">
            <span className="ea-loading-dot" />
            <span className="ea-loading-dot" />
            <span className="ea-loading-dot" />
        </div>
    );
}

function SlippageTag({ pct }) {
    if (pct == null) return <span className="ea-tag ea-tag--neutral">—</span>;
    const n = Number(pct);
    if (Math.abs(n) < 0.5) return <span className="ea-tag ea-tag--ok">{fmtPct(n)}</span>;
    if (n > 0) return <span className="ea-tag ea-tag--warn">{fmtPct(n)}</span>;
    return <span className="ea-tag ea-tag--good">{fmtPct(n)}</span>;
}

function PnlTag({ pnl }) {
    if (pnl == null) return <span className="ea-tag ea-tag--neutral">—</span>;
    const n = Number(pnl);
    if (n > 0) return <span className="ea-tag ea-tag--good">{fmtDollars(n)}</span>;
    if (n < 0) return <span className="ea-tag ea-tag--warn">{fmtDollars(n)}</span>;
    return <span className="ea-tag ea-tag--neutral">{fmtDollars(n)}</span>;
}

function SummaryMetric({ label, value, sub }) {
    return (
        <div className="ea-metric">
            <span className="ea-metric-value">{value}</span>
            <span className="ea-metric-label">{label}</span>
            {sub && <span className="ea-metric-sub">{sub}</span>}
        </div>
    );
}

function InfoBanner({ tone = "neutral", children }) {
    return <div className={`ea-info-banner ea-info-banner--${tone}`}>{children}</div>;
}

function ReconciliationBadge({ status }) {
    const normalized = String(status || "unknown").toLowerCase();
    return (
        <span className={`ea-recon-badge ea-recon-badge--${normalized}`}>
            {normalized.replaceAll("_", " ")}
        </span>
    );
}

function HealthMetric({ label, value }) {
    return (
        <div className="ea-health-metric">
            <span className="ea-health-value">{value}</span>
            <span className="ea-health-label">{label}</span>
        </div>
    );
}

// ── Coverage bar ────────────────────────────────────────────────────────────
function CoverageBar({ label, pct, count, total, warn }) {
    const pctNum = pct ?? 0;
    const color = warn
        ? "var(--color-warning, #f59e0b)"
        : pctNum >= 80
        ? "var(--color-success, #64d278)"
        : pctNum >= 40
        ? "var(--color-warning, #f59e0b)"
        : "var(--color-danger, #f87171)";
    return (
        <div className="ea-cov-row">
            <span className="ea-cov-label">{label}</span>
            <div className="ea-cov-bar-wrap">
                <div
                    className="ea-cov-bar-fill"
                    style={{ width: `${pctNum}%`, background: color }}
                />
            </div>
            <span className="ea-cov-pct" style={{ color }}>
                {pct != null ? `${pct}%` : "—"}
            </span>
            {count != null && total != null && (
                <span className="ea-cov-counts">{count} / {total}</span>
            )}
        </div>
    );
}

// ── Trust verdict badge ──────────────────────────────────────────────────────
function VerdictBadge({ verdict }) {
    const trusted = verdict === "trustworthy";
    return (
        <span className={`ea-verdict-badge ea-verdict-badge--${trusted ? "ok" : "warn"}`}>
            {trusted ? "Trustworthy" : "Gaps remaining"}
        </span>
    );
}

// ── Lifecycle stage pill ─────────────────────────────────────────────────────
function StagePill({ label, ok, count, note }) {
    return (
        <div className={`ea-stage-pill ea-stage-pill--${ok ? "ok" : "warn"}`}>
            <span className="ea-stage-icon">{ok ? "✓" : "!"}</span>
            <span className="ea-stage-label">{label}</span>
            {count != null && <span className="ea-stage-count">{count}</span>}
            {note && <span className="ea-stage-note">{note}</span>}
        </div>
    );
}

// ── Trust validation panel (lazy — only loaded on demand) ───────────────────
function TrustPanel({ days }) {
    const [vdata, setVdata] = useState(null);
    const [vloading, setVloading] = useState(false);
    const [verror, setVerror] = useState(null);
    const [loaded, setLoaded] = useState(false);

    const runValidation = useCallback(async () => {
        setVloading(true);
        setVerror(null);
        try {
            const { data: res } = await axios.get(
                buildApiUrl("/execution/execution-analysis/validate"),
                { params: { days }, withCredentials: true }
            );
            setVdata(res);
            setLoaded(true);
        } catch (e) {
            const d = e?.response?.data;
            setVerror(d?.error || d?.message || `Error ${e?.response?.status || "unknown"}`);
        } finally {
            setVloading(false);
        }
    }, [days]);

    if (!loaded && !vloading) {
        return (
            <div className="ea-trust-panel ea-trust-panel--idle">
                <p className="ea-trust-idle-text">
                    Validates timestamp ordering, lifecycle completeness, data coverage, and edge cases.
                </p>
                <button
                    type="button"
                    className="ea-trust-run-btn"
                    onClick={runValidation}
                >
                    Run validation
                </button>
            </div>
        );
    }

    if (vloading) {
        return <div className="ea-trust-panel"><LoadingDots /></div>;
    }

    if (verror) {
        return (
            <div className="ea-trust-panel">
                <div className="ea-error">{verror}</div>
                <button type="button" className="ea-trust-run-btn" onClick={runValidation}>
                    Retry
                </button>
            </div>
        );
    }

    if (!vdata) return null;

    const { coverage, time_alignment, lifecycle, edge_cases, trust } = vdata;
    const c = coverage || {};
    const ta = time_alignment || {};
    const lc = lifecycle || {};
    const ec = edge_cases || {};
    const tr = trust || {};

    return (
        <div className="ea-trust-panel">
            {/* Verdict */}
            <div className="ea-trust-header">
                <VerdictBadge verdict={tr.verdict} />
                <span className="ea-trust-window">Last {vdata.days_analyzed}d · since {vdata.since}</span>
                <button
                    type="button"
                    className="ea-trust-rerun-btn"
                    onClick={runValidation}
                    disabled={vloading}
                >
                    Re-run
                </button>
            </div>

            {/* Blocking issues */}
            {tr.blocking_issues?.length > 0 && (
                <div className="ea-trust-blocking">
                    {tr.blocking_issues.map((msg, i) => (
                        <div key={i} className="ea-trust-block-row">
                            <span className="ea-trust-block-icon">✗</span>
                            <span>{msg}</span>
                        </div>
                    ))}
                </div>
            )}

            {/* Coverage */}
            <div className="ea-trust-section">
                <div className="ea-trust-section-title">Data coverage</div>
                <CoverageBar
                    label="Alpaca order ID"
                    pct={c.pct_alpaca_id}
                    count={c.with_alpaca_id}
                    total={c.total_orders}
                />
                <CoverageBar
                    label="Fill data"
                    pct={c.pct_fill_data}
                    count={c.with_fill_data}
                    total={c.submitted_count}
                />
                <CoverageBar
                    label="Position key"
                    pct={c.pct_position_key}
                    count={c.with_position_key}
                    total={c.total_orders}
                    warn
                />
                <CoverageBar
                    label="Closed PnL"
                    pct={c.pct_pnl}
                    count={c.with_pnl}
                    total={c.with_position_key}
                />
            </div>

            {/* Time alignment */}
            <div className="ea-trust-section">
                <div className="ea-trust-section-title">
                    Time alignment
                    <span className={`ea-trust-check-badge ea-trust-check-badge--${ta.all_valid ? "ok" : "fail"}`}>
                        {ta.all_valid ? "All valid" : `${ta.anomalies?.length} anomaly/anomalies`}
                    </span>
                </div>
                {ta.anomalies?.length > 0 && (
                    <div className="ea-anomaly-list">
                        {ta.anomalies.map((a, i) => (
                            <div key={i} className="ea-anomaly-row">
                                <span className="ea-anomaly-type">{a.anomaly_type}</span>
                                <span className="ea-anomaly-sym">{a.symbol}</span>
                                <span className="ea-anomaly-detail">{a.detail}</span>
                            </div>
                        ))}
                    </div>
                )}
                {ta.all_valid && ta.checked > 0 && (
                    <div className="ea-trust-ok-note">
                        Checked {ta.checked} order{ta.checked !== 1 ? "s" : ""} — decision → fill → close ordering is valid.
                    </div>
                )}
                {ta.checked === 0 && (
                    <div className="ea-trust-ok-note ea-trust-ok-note--dim">
                        No orders with fill data yet — time alignment cannot be checked.
                    </div>
                )}
            </div>

            {/* Lifecycle stages */}
            <div className="ea-trust-section">
                <div className="ea-trust-section-title">Lifecycle completeness</div>
                <div className="ea-stage-row">
                    <StagePill label="Decision" ok count={lc.total} />
                    <span className="ea-stage-arrow">→</span>
                    <StagePill
                        label="Execution"
                        ok={lc.missing_alpaca_id?.length === 0}
                        count={lc.total - (lc.missing_alpaca_id?.length ?? 0)}
                        note={lc.missing_alpaca_id?.length ? `${lc.missing_alpaca_id.length} missing ID` : null}
                    />
                    <span className="ea-stage-arrow">→</span>
                    <StagePill
                        label="Fill"
                        ok={lc.missing_fill_data?.length === 0}
                        count={lc.total - (lc.missing_fill_data?.length ?? 0)}
                        note={lc.missing_fill_data?.length ? `${lc.missing_fill_data.length} need refresh` : null}
                    />
                    <span className="ea-stage-arrow">→</span>
                    <StagePill
                        label="Outcome"
                        ok={lc.missing_outcome_record?.length === 0}
                        count={lc.outcome_closed_count}
                        note={
                            lc.missing_outcome_record?.length
                                ? `${lc.missing_outcome_record.length} missing`
                                : lc.outcome_open?.length
                                ? `${lc.outcome_open.length} open`
                                : null
                        }
                    />
                </div>
                {lc.missing_fill_data?.length > 0 && (
                    <div className="ea-lifecycle-hint">
                        {lc.missing_fill_data.length} order(s) submitted but fill data not yet pulled.
                        Call <code>POST /api/execution/refresh-fill</code> for each Alpaca order ID.
                    </div>
                )}
                {lc.note && (
                    <div className="ea-trust-ok-note ea-trust-ok-note--dim">{lc.note}</div>
                )}
            </div>

            {/* Edge cases */}
            {(ec.partial_fill_count > 0 || ec.cancelled_count > 0 || ec.duplicate_alpaca_id_count > 0) && (
                <div className="ea-trust-section">
                    <div className="ea-trust-section-title">Edge cases</div>
                    <div className="ea-edge-row">
                        {ec.partial_fill_count > 0 && (
                            <div className="ea-edge-chip ea-edge-chip--warn">
                                {ec.partial_fill_count} partial fill{ec.partial_fill_count !== 1 ? "s" : ""}
                            </div>
                        )}
                        {ec.cancelled_count > 0 && (
                            <div className="ea-edge-chip ea-edge-chip--neutral">
                                {ec.cancelled_count} cancelled/error
                            </div>
                        )}
                        {ec.duplicate_alpaca_id_count > 0 && (
                            <div className="ea-edge-chip ea-edge-chip--danger">
                                {ec.duplicate_alpaca_id_count} duplicate broker ID{ec.duplicate_alpaca_id_count !== 1 ? "s" : ""}
                            </div>
                        )}
                    </div>
                    {ec.partial_fills?.map((p, i) => (
                        <div key={i} className="ea-edge-detail-row">
                            <span className="ea-edge-sym">{p.symbol}</span>
                            <span className="ea-edge-detail">
                                {p.fill_qty}/{p.ordered_qty} contracts filled
                                {p.fill_pct != null ? ` (${p.fill_pct}%)` : ""}
                                {p.slippage_pct != null ? ` · slip ${fmtPct(p.slippage_pct)}` : ""}
                            </span>
                        </div>
                    ))}
                </div>
            )}

            {/* Informational notes */}
            {tr.informational?.length > 0 && (
                <div className="ea-trust-notes">
                    {tr.informational.map((note, i) => (
                        <div key={i} className="ea-trust-note-row">· {note}</div>
                    ))}
                </div>
            )}
        </div>
    );
}

// ── Main component ───────────────────────────────────────────────────────────
export default function ExecutionAnalysis() {
    const [days, setDays] = useState(90);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showAll, setShowAll] = useState(false);
    const [showValidation, setShowValidation] = useState(false);
    const [reconcilingAuditId, setReconcilingAuditId] = useState(null);
    const [detailsAudit, setDetailsAudit] = useState(null);

    const load = useCallback(async (d) => {
        setLoading(true);
        setError(null);
        try {
            const { data: res } = await axios.get(
                buildApiUrl("/execution/execution-analysis"),
                { params: { days: d }, withCredentials: true }
            );
            setData(res);
        } catch (e) {
            const d = e?.response?.data;
            setError(d?.error || d?.message || `Error ${e?.response?.status || "unknown"}`);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load(days);
    }, [days, load]);

    const summary = data?.summary || {};
    const semantics = data?.semantics || {};
    const trades = data?.trades || [];
    const discrepancies = data?.discrepancies || [];
    const tradeDebug = data?.trade_debug || {};
    const visibleTrades = showAll ? trades : trades.slice(0, 10);
    const hasClosedOutcomeMetrics = (semantics.closed_outcomes_with_pnl || 0) > 0;
    const emptyMessage = semantics.state_message || `No app-originated orders found in the last ${days} days.`;
    const recentStatuses = tradeDebug?.savvy_orders?.recent || [];
    const diagnosis = tradeDebug?.diagnosis || [];
    const unreconciledAudits = tradeDebug?.unreconciled_audits || [];
    const reconciliationHealth = tradeDebug?.reconciliation_health || semantics.reconciliation_health || {};
    const autoResolutionNote = semantics.auto_resolution_note;
    const failedAudits = unreconciledAudits.filter((row) => String(row.reconciliation_status || "").toLowerCase() === "failed");
    const pendingAudits = unreconciledAudits.filter((row) => String(row.reconciliation_status || "").toLowerCase() !== "failed");

    const handleReconcile = useCallback(async (audit, options = {}) => {
        if (!audit?.audit_id) return;
        setReconcilingAuditId(audit.audit_id);
        try {
            const { data: res } = await axios.post(
                buildApiUrl("/system/reconcile-order"),
                {
                    audit_id: audit.audit_id,
                    order_id: audit.order_id,
                    symbol: audit.symbol,
                    force: Boolean(options.force),
                },
                { withCredentials: true }
            );
            if (res?.ok) {
                toast.success(options.force ? "Persistence retry override succeeded." : "Local trade persistence repaired.");
                await load(days);
            } else {
                toast.error(res?.error || "Reconciliation failed.");
            }
        } catch (e) {
            const d = e?.response?.data;
            toast.error(d?.error || d?.message || "Reconciliation failed.");
        } finally {
            setReconcilingAuditId(null);
        }
    }, [days, load]);

    return (
        <div className="ea-root">
            {/* Header */}
            <div className="ea-header">
                <div className="ea-title-row">
                    <h3 className="ea-title">Live vs Simulation</h3>
                    <div className="ea-days-toggle">
                        {DAYS_OPTIONS.map((opt) => (
                            <button
                                key={opt.value}
                                type="button"
                                className={`ea-days-btn ${days === opt.value ? "active" : ""}`}
                                onClick={() => setDays(opt.value)}
                                disabled={loading}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                </div>
                <p className="ea-subtitle">
                    Compare fill prices to simulated expectations. Identify execution gaps before scaling capital.
                </p>
            </div>

            {loading && <LoadingDots />}
            {error && !loading && <div className="ea-error">{error}</div>}

            {!loading && !error && data && (
                <>
                    {/* Summary metrics */}
                    <div className="ea-metrics-row">
                        <SummaryMetric label="App orders" value={semantics.submitted_app_orders ?? "—"} />
                        <SummaryMetric label="Live app orders" value={semantics.live_app_orders ?? "—"} />
                        <SummaryMetric label="Dry-run orders" value={semantics.dry_run_orders ?? "—"} />
                        <SummaryMetric label="Filled orders" value={semantics.filled_orders ?? "—"} />
                        <SummaryMetric
                            label="Fill rate"
                            value={summary.fill_success_rate != null ? `${summary.fill_success_rate}%` : "—"}
                            sub={
                                summary.submitted_count != null
                                    ? `${summary.filled_count ?? 0}/${summary.submitted_count} submitted${summary.partial_fill_count ? ` · ${summary.partial_fill_count} partial` : ""}`
                                    : null
                            }
                        />
                        <SummaryMetric label="Open outcomes" value={semantics.open_outcomes ?? "—"} />
                        <SummaryMetric label="Closed outcomes" value={semantics.closed_outcomes ?? "—"} />
                        <SummaryMetric
                            label="Avg slippage"
                            value={summary.avg_slippage_pct != null ? fmtPct(summary.avg_slippage_pct) : "—"}
                            sub={summary.avg_slippage != null ? `$${fmt2(summary.avg_slippage)}` : null}
                        />
                        <SummaryMetric label="Avg fill time" value={fmtSeconds(summary.avg_time_to_fill_seconds)} />
                        <SummaryMetric
                            label="Win rate"
                            value={hasClosedOutcomeMetrics && summary.win_rate != null ? `${summary.win_rate}%` : "—"}
                            sub={semantics.closed_outcomes_with_pnl ? `${semantics.closed_outcomes_with_pnl} closed with PnL` : null}
                        />
                        <SummaryMetric
                            label="Total PnL"
                            value={hasClosedOutcomeMetrics && summary.total_pnl != null ? fmtDollars(summary.total_pnl) : "—"}
                            sub={hasClosedOutcomeMetrics && summary.avg_pnl != null ? `avg ${fmtDollars(summary.avg_pnl)}` : null}
                        />
                        {summary.sim_vs_outcome_correlation != null && (
                            <SummaryMetric
                                label="Score↔Outcome"
                                value={`r=${summary.sim_vs_outcome_correlation}`}
                                sub={Math.abs(summary.sim_vs_outcome_correlation) >= 0.3 ? "strong" : "weak"}
                            />
                        )}
                    </div>

                    {semantics.persistence_warning && (
                        <InfoBanner tone="warn">
                            <div className="ea-banner-stack">
                                <div>{semantics.persistence_warning}</div>
                                {tradeDebug?.reconciliation_available && pendingAudits.length > 0 && (
                                    <>
                                        <button
                                            type="button"
                                            className="ea-reconcile-btn"
                                            onClick={() => handleReconcile(pendingAudits[0])}
                                            disabled={reconcilingAuditId != null}
                                        >
                                            {reconcilingAuditId === pendingAudits[0].audit_id ? "Retrying…" : "Retry Persistence"}
                                        </button>
                                        <button
                                            type="button"
                                            className="ea-reconcile-btn ea-reconcile-btn--secondary"
                                            onClick={() => setDetailsAudit(pendingAudits[0])}
                                        >
                                            View details
                                        </button>
                                    </>
                                )}
                            </div>
                        </InfoBanner>
                    )}
                    {autoResolutionNote && (
                        <InfoBanner tone="ok">{autoResolutionNote}</InfoBanner>
                    )}
                    {semantics.earnings_note && (
                        <InfoBanner tone="neutral">{semantics.earnings_note}</InfoBanner>
                    )}
                    {semantics.wheel_note && (
                        <InfoBanner tone="neutral">{semantics.wheel_note}</InfoBanner>
                    )}

                    {/* Discrepancy strip */}
                    {hasClosedOutcomeMetrics && discrepancies.length > 0 && (
                        <div className="ea-discrepancy-section">
                            <div className="ea-discrepancy-header">
                                {discrepancies.length} execution gap{discrepancies.length !== 1 ? "s" : ""} detected
                            </div>
                            <div className="ea-discrepancy-list">
                                {discrepancies.map((d, i) => (
                                    <div
                                        key={i}
                                        className={`ea-discrepancy-row ea-discrepancy-row--${d.discrepancy_type === "high_score_loser" ? "warn" : "info"}`}
                                    >
                                        <span className="ea-disc-symbol">{d.symbol}</span>
                                        <span className="ea-disc-action">
                                            {EA_ACTION_LABELS[d.action] || d.action}
                                        </span>
                                        {resolveTradeStrategy(d) && (
                                            <span className="ea-disc-strategy">{resolveTradeStrategy(d)}</span>
                                        )}
                                        <span className="ea-disc-tag">
                                            {d.discrepancy_type === "high_score_loser"
                                                ? `Score ${d.sim_score} → loss`
                                                : `Score ${d.sim_score} → win`}
                                        </span>
                                        <PnlTag pnl={d.realized_pnl} />
                                        {d.slippage_pct != null && (
                                            <span className="ea-disc-slip">slip {fmtPct(d.slippage_pct)}</span>
                                        )}
                                        <span className="ea-disc-date">{fmtDate(d.created_at)}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Per-trade table */}
                    {trades.length === 0 ? (
                        <div className="ea-empty">
                            {emptyMessage}
                        </div>
                    ) : (
                        <div className="ea-table-wrap">
                            <table className="ea-table">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Symbol</th>
                                        <th>Action</th>
                                        <th>Expected $</th>
                                        <th>Fill $</th>
                                        <th>Slippage</th>
                                        <th>Fill time</th>
                                        <th>Sim score</th>
                                        <th>Outcome</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {visibleTrades.map((t, i) => {
                                        const stratLabel  = resolveTradeStrategy(t);
                                        const modeLabel   = resolveTradeMode(t);
                                        const actionLabel = EA_ACTION_LABELS[t.action] || t.action;
                                        return (
                                        <tr
                                            key={t.savvy_order_id || i}
                                            className={t.fill_price == null ? "ea-row--unfilled" : ""}
                                        >
                                            <td className="ea-cell-date">{fmtDate(t.created_at)}</td>
                                            <td className="ea-cell-symbol">{t.symbol || "—"}</td>
                                            <td className="ea-cell-action">
                                                <div className="ea-action-stack">
                                                    <span className="ea-action-chip">{actionLabel || "—"}</span>
                                                    {stratLabel && (
                                                        <span className="ea-strategy-sub">{stratLabel}</span>
                                                    )}
                                                    {modeLabel && (
                                                        <span className={`ea-mode-badge ea-mode-badge--${modeLabel}`}>
                                                            {modeLabel === "auto" ? "Auto" : "Manual"}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td>{t.expected_price != null ? `$${fmt2(t.expected_price)}` : "—"}</td>
                                            <td>
                                                {t.fill_price != null ? (
                                                    <>
                                                        ${fmt2(t.fill_price)}
                                                        {t.status === "partially_filled" && (
                                                            <span className="ea-partial-tag"> partial</span>
                                                        )}
                                                    </>
                                                ) : (
                                                    <span className="ea-unfilled">unfilled</span>
                                                )}
                                            </td>
                                            <td><SlippageTag pct={t.slippage_pct} /></td>
                                            <td>{fmtSeconds(t.time_to_fill_seconds)}</td>
                                            <td>
                                                {(t.sim_score ?? t.trade_score) != null ? (
                                                    <span className="ea-score">{t.sim_score ?? t.trade_score}</span>
                                                ) : "—"}
                                            </td>
                                            <td><PnlTag pnl={t.realized_pnl} /></td>
                                        </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                            {trades.length > 10 && (
                                <button
                                    type="button"
                                    className="ea-show-more"
                                    onClick={() => setShowAll((s) => !s)}
                                >
                                    {showAll ? "Show less" : `Show all ${trades.length} trades`}
                                </button>
                            )}
                        </div>
                    )}

                    <div className="ea-debug-drawer">
                        <div className="ea-debug-header">Diagnostics</div>
                        <div className="ea-health-row">
                            <HealthMetric label="Pending" value={reconciliationHealth.pending ?? 0} />
                            <HealthMetric label="Resolved" value={reconciliationHealth.resolved ?? 0} />
                            <HealthMetric label="Failed" value={reconciliationHealth.failed ?? 0} />
                            <HealthMetric label="Success rate" value={reconciliationHealth.success_rate != null ? `${reconciliationHealth.success_rate}%` : "—"} />
                        </div>
                        <div className="ea-debug-grid">
                            <div className="ea-debug-card">
                                <div className="ea-debug-title">DB environment</div>
                                <div className="ea-debug-line">
                                    {tradeDebug?.environment?.env || "—"} · {tradeDebug?.environment?.db_name || "—"}
                                </div>
                                <div className="ea-debug-line">
                                    {tradeDebug?.environment?.db_host || "—"}:{tradeDebug?.environment?.db_port || "—"}
                                </div>
                            </div>
                            <div className="ea-debug-card">
                                <div className="ea-debug-title">Order counts</div>
                                <div className="ea-debug-line">dry_run=true: {tradeDebug?.savvy_orders?.dry_run_true ?? 0}</div>
                                <div className="ea-debug-line">dry_run=false: {tradeDebug?.savvy_orders?.dry_run_false ?? 0}</div>
                            </div>
                        </div>

                        {recentStatuses.length > 0 && (
                            <div className="ea-debug-section">
                                <div className="ea-debug-title">Recent order statuses</div>
                                <div className="ea-debug-list">
                                    {recentStatuses.map((row, idx) => (
                                        <div key={`${row.id || idx}-${row.created_at || idx}`} className="ea-debug-row">
                                            <span className="ea-debug-symbol">{row.symbol || "—"}</span>
                                            <span className="ea-debug-chip">{row.action || "—"}</span>
                                            <span className="ea-debug-chip">{row.status || "—"}</span>
                                            <span className="ea-debug-chip">{row.dry_run ? "dry_run" : "live"}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {diagnosis.length > 0 && (
                            <div className="ea-debug-section">
                                <div className="ea-debug-title">Diagnosis</div>
                                <div className="ea-debug-diagnosis">
                                    {diagnosis.map((msg, idx) => (
                                        <div key={idx} className="ea-debug-note">· {msg}</div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {pendingAudits.length > 0 && (
                            <div className="ea-debug-section">
                                <div className="ea-debug-title">Pending reconciliations</div>
                                <div className="ea-debug-list">
                                    {pendingAudits.map((row) => (
                                        <div key={row.audit_id} className="ea-debug-row ea-debug-row--spread">
                                            <span className="ea-debug-symbol">{row.symbol || "—"}</span>
                                            <span className="ea-debug-chip">audit {row.audit_id}</span>
                                            <span className="ea-debug-chip">{row.strategy || "—"}</span>
                                            <span className="ea-debug-chip">{row.order_id || "no order id"}</span>
                                            <ReconciliationBadge status={row.reconciliation_status} />
                                            <span className="ea-debug-chip">attempts {row.reconciliation_attempts ?? 0}</span>
                                            <button
                                                type="button"
                                                className="ea-reconcile-btn"
                                                onClick={() => handleReconcile(row)}
                                                disabled={reconcilingAuditId != null}
                                            >
                                                {reconcilingAuditId === row.audit_id ? "Retrying…" : "Retry Persistence"}
                                            </button>
                                            <button
                                                type="button"
                                                className="ea-reconcile-btn ea-reconcile-btn--secondary"
                                                onClick={() => setDetailsAudit(row)}
                                            >
                                                View details
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {failedAudits.length > 0 && (
                            <div className="ea-debug-section">
                                <div className="ea-debug-title">Failed reconciliations</div>
                                <div className="ea-debug-diagnosis">
                                    <div className="ea-debug-note">
                                        Trade executed but was not recorded locally. Execution Analysis may be incomplete.
                                    </div>
                                </div>
                                <div className="ea-debug-list">
                                    {failedAudits.map((row) => (
                                        <div key={row.audit_id} className="ea-debug-row ea-debug-row--spread ea-debug-row--failed">
                                            <span className="ea-debug-symbol">{row.symbol || "—"}</span>
                                            <span className="ea-debug-chip">audit {row.audit_id}</span>
                                            <span className="ea-debug-chip">{row.strategy || "—"}</span>
                                            <ReconciliationBadge status={row.reconciliation_status} />
                                            <span className="ea-debug-chip">attempts {row.reconciliation_attempts ?? 0}</span>
                                            <button
                                                type="button"
                                                className="ea-reconcile-btn"
                                                onClick={() => handleReconcile(row, { force: true })}
                                                disabled={reconcilingAuditId != null}
                                            >
                                                {reconcilingAuditId === row.audit_id ? "Retrying…" : "Retry Override"}
                                            </button>
                                            <button
                                                type="button"
                                                className="ea-reconcile-btn ea-reconcile-btn--secondary"
                                                onClick={() => setDetailsAudit(row)}
                                            >
                                                View details
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {detailsAudit && (
                        <div className="ea-modal-overlay" onClick={() => setDetailsAudit(null)}>
                            <div className="ea-modal" onClick={(e) => e.stopPropagation()}>
                                <div className="ea-modal-head">
                                    <div className="ea-modal-title">Reconciliation details</div>
                                    <button type="button" className="ea-modal-close" onClick={() => setDetailsAudit(null)}>×</button>
                                </div>
                                <div className="ea-modal-body">
                                    <div className="ea-modal-row"><strong>Audit</strong> <span>{detailsAudit.audit_id}</span></div>
                                    <div className="ea-modal-row"><strong>Symbol</strong> <span>{detailsAudit.symbol || "—"}</span></div>
                                    <div className="ea-modal-row"><strong>Strategy</strong> <span>{detailsAudit.strategy || "—"}</span></div>
                                    <div className="ea-modal-row"><strong>Order ID</strong> <span>{detailsAudit.order_id || "—"}</span></div>
                                    <div className="ea-modal-row"><strong>Status</strong> <ReconciliationBadge status={detailsAudit.reconciliation_status} /></div>
                                    <div className="ea-modal-row"><strong>Attempts</strong> <span>{detailsAudit.reconciliation_attempts ?? 0}</span></div>
                                    {detailsAudit.last_reconciliation_at && (
                                        <div className="ea-modal-row"><strong>Last tried</strong> <span>{detailsAudit.last_reconciliation_at}</span></div>
                                    )}
                                    {tradeDebug?.recent_reconciliation_logs?.length > 0 && (
                                        <div className="ea-modal-log">
                                            <div className="ea-debug-title">Recent attempts</div>
                                            {tradeDebug.recent_reconciliation_logs
                                                .filter((log) => log.audit_id === detailsAudit.audit_id)
                                                .map((log) => (
                                                    <div key={log.id} className="ea-modal-log-row">
                                                        <span>{log.created_at}</span>
                                                        <ReconciliationBadge status={log.status} />
                                                        <span>attempt {log.attempt_number}</span>
                                                        <span>{log.error_message || "ok"}</span>
                                                    </div>
                                                ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Trust validation (lazy) */}
                    <div className="ea-validation-section">
                        <button
                            type="button"
                            className="ea-validation-toggle"
                            onClick={() => setShowValidation((v) => !v)}
                        >
                            <span>Trust validation</span>
                            <span className="ea-validation-chevron">{showValidation ? "▴" : "▾"}</span>
                        </button>
                        {showValidation && <TrustPanel days={days} />}
                    </div>
                </>
            )}
        </div>
    );
}
